import type { IApiClient, StreamHandle } from './interface'
import type {
  DaemonHealth,
  Interaction,
  InteractionAnswer,
  NormalizedPhase,
  NormalizedRun,
  QueueEntry,
  RunCheckpoint,
  RunDecision,
  RunStatus,
  Task,
  TranscriptEvent,
  TranscriptEventType,
  KnowledgeEntry,
  ProjectedAgent,
  ProjectedAgentDetail,
} from './types'

function uid(): string {
  return Math.random().toString(36).slice(2, 10)
}

function iso(offsetMs = 0): string {
  return new Date(Date.now() + offsetMs).toISOString()
}

function makePhases(runStatus: RunStatus): NormalizedPhase[] {
  const all: NormalizedPhase[] = [
    { id: uid(), title: 'Plan', detail: 'Decomposing task', status: 'completed', startedAt: iso(-60000), finishedAt: iso(-50000), agentCount: 1, attempt: 1, durationMs: 10000 },
    { id: uid(), title: 'Implement', detail: 'Writing code', status: runStatus === 'running' ? 'running' : 'completed', startedAt: iso(-50000), finishedAt: runStatus === 'running' ? undefined : iso(-10000), agentCount: 3, attempt: 2, durationMs: runStatus === 'running' ? undefined : 40000 },
    { id: uid(), title: 'Review', detail: 'Checking correctness', status: runStatus === 'completed' ? 'completed' : 'queued', startedAt: runStatus === 'completed' ? iso(-10000) : undefined, finishedAt: runStatus === 'completed' ? iso(-2000) : undefined, agentCount: 0, attempt: runStatus === 'completed' ? 1 : null, durationMs: runStatus === 'completed' ? 8000 : null },
  ]
  return all
}

function makeDecisions(): RunDecision[] {
  return [
    {
      phaseId: 'Plan',
      decision: 'advance',
      reason: 'Plan covers every acceptance criterion; decomposition is sound and no open questions remain.',
      confidence: 0.92,
      risk: 'low',
      timestamp: iso(-48000),
    },
    {
      phaseId: 'Implement',
      decision: 'rework',
      reason: 'Two unit tests fail on the edge case where the queue is drained mid-dispatch; sending back for a fix.',
      confidence: 0.71,
      risk: 'medium',
      timestamp: iso(-20000),
    },
  ]
}

function makeCheckpoints(): RunCheckpoint[] {
  return [
    { number: 1, phaseId: 'Implement', reason: 'status-change', timestamp: iso(-30000) },
  ]
}

// Mixed-status set — the gallery wall shows runs of ALL statuses, newest first.
const MOCK_RUNS: NormalizedRun[] = [
  { id: 'run-alpha', taskId: 'TASK-001', title: 'scaffold auth module', status: 'completed', phases: makePhases('completed'), activeAgents: 0, createdAt: iso(-120000), updatedAt: iso(-2000), decisions: makeDecisions(), checkpoints: makeCheckpoints() },
  { id: 'run-beta', taskId: 'TASK-002', title: 'add dashboard charts', status: 'running', phases: makePhases('running'), activeAgents: 3, createdAt: iso(-90000), updatedAt: iso(-500), decisions: makeDecisions(), checkpoints: makeCheckpoints() },
  { id: 'run-gamma', taskId: 'TASK-003', title: 'fix race condition in queue', status: 'running', phases: makePhases('running'), activeAgents: 2, createdAt: iso(-45000), updatedAt: iso(-200), decisions: makeDecisions(), checkpoints: makeCheckpoints() },
  { id: 'run-delta', taskId: 'TASK-004', title: 'write integration tests', status: 'queued', phases: makePhases('queued'), activeAgents: 0, createdAt: iso(-5000), updatedAt: iso(-5000), decisions: [], checkpoints: [] },
  { id: 'run-epsilon', taskId: 'TASK-007', title: 'bump deploy pipeline', status: 'failed', phases: makePhases('failed'), activeAgents: 0, createdAt: iso(-300000), updatedAt: iso(-240000), decisions: makeDecisions(), checkpoints: makeCheckpoints() },
  { id: 'run-zeta', taskId: 'TASK-008', title: 'refactor plugin loader', status: 'cancelled', phases: makePhases('cancelled'), activeAgents: 0, createdAt: iso(-420000), updatedAt: iso(-400000), decisions: [], checkpoints: [] },
  { id: 'run-eta', taskId: 'TASK-009', title: 'harden MCP oauth broker', status: 'completed', phases: makePhases('completed'), activeAgents: 0, createdAt: iso(-540000), updatedAt: iso(-480000), decisions: makeDecisions(), checkpoints: makeCheckpoints() },
  { id: 'run-theta', taskId: 'TASK-010', title: 'ship env reaper cron', status: 'completed', phases: makePhases('completed'), activeAgents: 0, createdAt: iso(-660000), updatedAt: iso(-600000), decisions: [], checkpoints: [] },
]

// Backlog spread across every board column so the KANBAN wall is populated in
// mock mode (VITE_MOCK_DATA=true).
const MOCK_KNOWLEDGE: KnowledgeEntry[] = [
  { id: 'K-repo-portal', title: 'animus-launchapp (the portal)', entityKind: 'repo', relationships: [
    { to_id: 'K-svc-daemon', type: 'depends_on', confidence: 0.95 },
    { to_id: 'K-concept-mcp', type: 'exposes', confidence: 0.8 } ] },
  { id: 'K-repo-cli', title: 'animus-cli (the kernel)', entityKind: 'repo', relationships: [
    { to_id: 'K-svc-daemon', type: 'builds', confidence: 0.9 },
    { to_id: 'K-repo-protocol', type: 'depends_on', confidence: 0.85 } ] },
  { id: 'K-repo-protocol', title: 'animus-protocol', entityKind: 'repo', relationships: [
    { to_id: 'K-concept-plugin', type: 'defines', confidence: 0.9 } ] },
  { id: 'K-svc-daemon', title: 'The daemon', entityKind: 'service', relationships: [
    { to_id: 'K-concept-runner', type: 'spawns', confidence: 0.7 },
    { to_id: 'K-svc-railway', type: 'deployed_on', confidence: 0.6 } ] },
  { id: 'K-svc-railway', title: 'Railway deploy', entityKind: 'service', relationships: [
    { to_id: 'K-does-not-exist', type: 'references', confidence: 0.4 } ] },
  { id: 'K-concept-mcp', title: 'MCP (Model Context Protocol)', entityKind: 'concept', relationships: [
    { to_id: 'K-concept-plugin', type: 'related', confidence: 0.5 } ] },
  { id: 'K-concept-plugin', title: 'Plugin protocol', entityKind: 'concept', relationships: [] },
  { id: 'K-concept-runner', title: 'Workflow runner', entityKind: 'concept', relationships: [
    { to_id: 'K-repo-cli', type: 'built_against', confidence: 0.75 } ] },
]

const MOCK_TASKS: Task[] = [
  { id: 'TASK-850', title: 'Arena gate + OAuth scopes', status: 'done', priority: 'p1', labels: ['arena'], assignee: 'sam' },
  { id: 'TASK-851', title: 'Scaffold FPV shell', status: 'done', priority: 'p2', labels: ['arena'] },
  { id: 'TASK-852', title: 'Build the district world', status: 'in-progress', priority: 'p1', labels: ['arena', 'world'], assignee: 'claude' },
  { id: 'TASK-853', title: 'Interactions + kanban board', status: 'in-progress', priority: 'p0', assignee: 'claude' },
  { id: 'TASK-807', title: 'Env reaper cron', status: 'ready', priority: 'p2' },
  { id: 'TASK-846', title: 'Portal MCP surface gaps', status: 'ready', priority: 'p3', labels: ['portal'] },
  { id: 'TASK-837', title: 'Thin launcher for console', status: 'ready', priority: 'p1' },
  { id: 'TASK-822', title: 'Delete bad node-image tags', status: 'blocked', priority: 'p1', assignee: 'sam' },
  { id: 'TASK-825', title: 'v0.3.8 node lockstep', status: 'blocked', priority: 'p0', labels: ['infra'] },
  { id: 'TASK-900', title: 'Old R3F removal', status: 'cancelled', priority: 'p3' },
]

const MOCK_QUEUE: QueueEntry[] = [
  { id: 'q-1', taskId: 'TASK-005', title: 'Update API docs', position: 1, status: 'pending', submittedAt: iso(-3000) },
  { id: 'q-2', taskId: 'TASK-006', title: 'Migrate DB schema', position: 2, status: 'pending', submittedAt: iso(-1000) },
]

const TOOL_NAMES = ['Read', 'Edit', 'Bash', 'Write', 'Agent', 'WebSearch']
const CODE_SNIPPETS = [
  'const result = await client.query(GET_RUNS, {}).toPromise()',
  'function scaffoldRepo(name: string) { return exec(`gh repo create ${name}`) }',
  'export class WorkflowRunner { async dispatch(task: Task) { … } }',
  'await Promise.all(phases.map(p => agent(p.prompt, { schema: SCHEMA })))',
  'return pipeline(items, stageA, stageB, stageC)',
]

function generateStreamEvents(runId: string, phaseId: string): TranscriptEvent[] {
  const events: TranscriptEvent[] = []
  const types: TranscriptEventType[] = ['text', 'tool_call', 'tool_result', 'thinking', 'tool_info', 'text', 'tool_call', 'tool_result', 'text']
  for (let i = 0; i < types.length; i++) {
    const type = types[i]
    let content = ''
    if (type === 'thinking') content = 'Let me think about this approach carefully…'
    else if (type === 'tool_call') content = JSON.stringify({ name: TOOL_NAMES[i % TOOL_NAMES.length], input: { path: '/src/index.ts' } })
    else if (type === 'tool_info') content = JSON.stringify({ name: TOOL_NAMES[(i + 2) % TOOL_NAMES.length] })
    else if (type === 'tool_result') content = CODE_SNIPPETS[i % CODE_SNIPPETS.length]
    else content = `Processing step ${i + 1} of the implementation plan for run ${runId}.`
    events.push({ id: uid(), runId, phaseId, agentId: `agent-${i % 3}`, type, content, timestamp: iso(i * 500) })
  }
  return events
}

const MOCK_INTERACTIONS_TEMPLATE: Interaction[] = [
  {
    id: 'int-1',
    runId: 'run-beta',
    type: 'approval',
    prompt: 'Run destructive build step?',
    createdAt: iso(-30000),
    toolName: 'Bash',
    toolArguments: { command: 'rm -rf dist && npm run build' },
  },
  {
    id: 'int-2',
    runId: 'run-gamma',
    type: 'question',
    prompt: 'Should I also add unit tests for the new module?',
    createdAt: iso(-15000),
    questions: [
      {
        question: 'Should I also add unit tests for the new module?',
        header: 'Testing',
        options: [
          { label: 'Yes', description: 'Add tests alongside the implementation' },
          { label: 'No', description: 'Skip tests for now' },
        ],
        multi_select: false,
      },
    ],
  },
]

function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

/**
 * Mock API client — provides fake data so world/gameplay code works
 * before the portal gate task ships. Enable with VITE_MOCK_DATA=true.
 */
export class MockApiClient implements IApiClient {
  /** Stable event cache so reconnects with a cursor see the same event IDs. */
  private streamEventCache = new Map<string, TranscriptEvent[]>()
  /** Per-instance pending interactions (so answered interactions clear for this session). */
  private pendingInteractions: Interaction[] = MOCK_INTERACTIONS_TEMPLATE.map((i) => ({ ...i }))
  /** Per-instance task backlog so callers cannot mutate the fixture. */
  private tasks: Task[] = MOCK_TASKS.map((t) => ({ ...t }))
  async getDaemonHealth(): Promise<DaemonHealth> {
    await delay(50)
    return {
      status: 'healthy',
      uptime: 3600 * 12,
      activeRuns: 2,
      activeAgents: 2,
      poolSize: 8,
      queueDepth: MOCK_QUEUE.length,
      version: '0.1.0-mock',
      checkedAt: iso(),
    }
  }

  async listAgents(): Promise<ProjectedAgent[]> {
    await delay(40)
    return [
      { id: 'planner', name: 'Planner' },
      { id: 'triage', name: 'Triage' },
      { id: 'scribe', name: 'Scribe' },
    ]
  }

  async getAgentDetail(id: string): Promise<ProjectedAgentDetail> {
    await delay(30)
    const agent = (await this.listAgents()).find((candidate) => candidate.id === id)
    if (!agent) throw new Error(`Mock: agent ${id} not found`)
    return {
      ...agent,
      canInspect: true,
      configuration: {
        model: 'mock/frontier',
        tool: 'codex',
        provider: 'mock',
        description: 'A project-scoped mock agent.',
        mcpServers: ['animus'],
        skills: ['planning'],
        memory: { enabled: true },
        communication: { enabled: true, channels: ['project'] },
        reasoningEffort: 'high',
        toolPolicy: { allow: ['read'], deny: ['deploy'] },
      },
    }
  }

  async listRuns(_limit = 20): Promise<NormalizedRun[]> {
    await delay(80)
    return MOCK_RUNS
  }

  async getRun(id: string): Promise<NormalizedRun> {
    await delay(40)
    const run = MOCK_RUNS.find((r) => r.id === id)
    if (!run) throw new Error(`Mock: run ${id} not found`)
    return run
  }

  streamTranscript(
    runId: string,
    onEvent: (event: TranscriptEvent) => void,
    onError?: (err: Error) => void,
    cursor?: string,
  ): StreamHandle {
    const run = MOCK_RUNS.find((r) => r.id === runId)
    if (!run) {
      onError?.(new Error(`Mock: run ${runId} not found`))
      return { cancel: () => {} }
    }

    // Cache events per runId so event IDs are stable across reconnects
    if (!this.streamEventCache.has(runId)) {
      const phaseId = run.phases[1]?.id ?? uid()
      this.streamEventCache.set(runId, generateStreamEvents(runId, phaseId))
    }
    const events = this.streamEventCache.get(runId)!

    // Resume from the event after the cursor (if provided)
    let index = 0
    if (cursor) {
      const cursorIdx = events.findIndex((e) => e.id === cursor)
      if (cursorIdx !== -1) index = cursorIdx + 1
    }

    let cancelled = false

    const tick = () => {
      if (cancelled || index >= events.length) return
      onEvent(events[index++])
      setTimeout(tick, 400 + Math.random() * 600)
    }

    setTimeout(tick, 200)

    return {
      cancel: () => { cancelled = true },
    }
  }

  async listKnowledge(): Promise<KnowledgeEntry[]> {
    await delay(70)
    return MOCK_KNOWLEDGE
  }

  async listTasks(): Promise<Task[]> {
    await delay(70)
    return this.tasks.map((t) => ({ ...t }))
  }

  async listQueue(): Promise<QueueEntry[]> {
    await delay(60)
    return MOCK_QUEUE
  }

  async listInteractions(runId: string): Promise<Interaction[]> {
    await delay(40)
    // Empty runId means "every run" so the projection owner can fetch once.
    // does ONE bucketed fetch per poll instead of N per-run fetches.
    if (!runId) return [...this.pendingInteractions]
    return this.pendingInteractions.filter((i) => i.runId === runId)
  }

  async respondToInteraction(interactionId: string, _answer: InteractionAnswer): Promise<void> {
    await delay(100)
    const idx = this.pendingInteractions.findIndex((i) => i.id === interactionId)
    if (idx === -1) throw new Error(`Mock: interaction ${interactionId} not found or already answered`)
    this.pendingInteractions.splice(idx, 1)
  }

  async getMe(): Promise<Record<string, unknown>> {
    await delay(30)
    return {
      sub: 'mock-user-001',
      name: 'Dev User (mock)',
      email: 'dev@mock.local',
      roles: ['arena:read', 'arena:interact'],
    }
  }

}
